import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Stack,
  Typography,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Description as JournalIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  TaskAlt as TaskAltIcon,
  PhotoLibrary as PhotoIcon,
} from '@mui/icons-material';
import { SiteVisitLog } from '../../../models/SiteVisitLog';
import { SitePhotoService } from '../../../services/SitePhotoService';
import RichTextEditor from '../../../components/editor/RichTextEditor';

/** attachments_csv → photo ID 배열. 빈 값/잘못된 토큰은 무시. */
const parseAttachmentIds = (csv?: string): number[] =>
  (csv ?? '')
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n) && n > 0);

interface VisitLogDetailDialogProps {
  open: boolean;
  log: SiteVisitLog | null;
  onClose: () => void;
  /** 제공 시 다이얼로그 상단에 수정 버튼 노출. */
  onEdit?: (log: SiteVisitLog) => void;
  /** 제공 시 다이얼로그 상단에 삭제 버튼 노출. */
  onDelete?: (log: SiteVisitLog) => void;
}

const formatDateTimeLong = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateTimeShort = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 출장 일지 상세 보기 다이얼로그.
 * 리스트의 항목을 클릭하면 띄워서 작업 내용·후속 조치를 잘림 없이 본다.
 *
 * onEdit/onDelete 제공 시 상단에 액션 버튼 노출.
 */
const VisitLogDetailDialog: React.FC<VisitLogDetailDialogProps> = ({
  open,
  log,
  onClose,
  onEdit,
  onDelete,
}) => {
  if (!log) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            출장 일지가 선택되지 않았습니다.
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <JournalIcon fontSize="small" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {log.purpose}
          </Typography>
        </Box>
        {onEdit && (
          <IconButton size="small" onClick={() => onEdit(log)} aria-label="수정">
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        {onDelete && (
          <IconButton size="small" color="error" onClick={() => onDelete(log)} aria-label="삭제">
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton onClick={onClose} size="small" aria-label="닫기">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* 방문일 */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <ScheduleIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              방문일
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ pl: 3.5 }}>
            {formatDateTimeLong(log.visited_at)}
          </Typography>
        </Box>

        {/* 방문자 */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              방문자
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ pl: 3.5 }}>
            {log.visitor_name}
          </Typography>
        </Box>

        {log.work_summary && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ display: 'block', mb: 0.5 }}
              >
                작업 내용
              </Typography>
              <Box sx={{ '& .ProseMirror': { fontSize: '0.875rem' } }}>
                <RichTextEditor value={log.work_summary} readOnly />
              </Box>
            </Box>
          </>
        )}

        {log.next_action && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ mb: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <TaskAltIcon fontSize="small" color="primary" />
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="primary.main"
                >
                  후속 조치
                </Typography>
              </Stack>
              <Box sx={{ pl: 3.5, '& .ProseMirror': { fontSize: '0.875rem' } }}>
                <RichTextEditor value={log.next_action} readOnly />
              </Box>
            </Box>
          </>
        )}

        {parseAttachmentIds(log.attachments_csv).length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ mb: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <PhotoIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  첨부 사진 ({parseAttachmentIds(log.attachments_csv).length}장)
                </Typography>
              </Stack>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 3.5 }}>
                {parseAttachmentIds(log.attachments_csv).map((id) => (
                  <Box
                    key={id}
                    component="a"
                    href={SitePhotoService.fileUrl(id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      width: 96,
                      height: 96,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'block',
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="img"
                      src={SitePhotoService.fileUrl(id)}
                      alt="첨부 사진"
                      loading="lazy"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}

        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ rowGap: 0.25 }}>
          <Typography variant="caption" color="text.secondary">
            등록: {formatDateTimeShort(log.created_at)}
          </Typography>
          {log.updated_at && log.updated_at !== log.created_at && (
            <Typography variant="caption" color="text.secondary">
              수정: {formatDateTimeShort(log.updated_at)}
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VisitLogDetailDialog;
